import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import api from 'helpers/api'
import { IProject } from 'types'
import { useAuth } from 'hooks/auth'
import { useApplicationStore } from 'hooks/application'
import useSocket from 'hooks/useSocket'
import { subscribeSocketRoom } from 'helpers/socketClient'
import { createResourcesUpdatedBurst, RESOURCES_UPDATED_WINDOW_MS } from 'helpers/resourcesUpdated'

/**
 * Up to how much longer than a burst window to wait before re-reading after `hubs_updated`. One site
 * change can reach every open tab in an organisation at once, so the re-reads are spread out.
 */
export const HUBS_UPDATED_JITTER_MS = 2_000

export interface HubsDataStore {
  projects: IProject[]
  loading: boolean
  reload: () => void
  /** Goes up each time the hub list is re-read after the first load, so what derives from it can re-read too. */
  revision: number
}

const HubsContext = React.createContext<HubsDataStore | undefined>(undefined)

export const HubsDataProvider: React.FC<PropsWithChildren> = ({ children }) => {
  'use no memo'
  const { user } = useAuth()
  const { displayErrorMessage } = useApplicationStore()
  const { socket } = useSocket()

  const [projects, setProjects] = useState<IProject[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [hasFetched, setHasFetched] = useState<boolean>(false)
  const [revision, setRevision] = useState<number>(0)
  // Who's signed in now, so a response can tell whether it's still wanted: if
  // the account changes (another tab switching accounts) while a request is in
  // flight, the old account's hubs must not land in the new account's list.
  const currentUserId = useRef<string | undefined>(user?.publicId)
  currentUserId.current = user?.publicId
  // The latest request: a re-read can overlap an earlier one, and the earlier one's answer, which may
  // predate the change that caused the re-read, must not land after it.
  const latestRequest = useRef(0)

  const fetchProjects = useCallback(() => {
    if (!user || !user.publicId) return
    const requestedFor = user.publicId
    const request = ++latestRequest.current
    const superseded = () => requestedFor !== currentUserId.current || request !== latestRequest.current
    setLoading(true)
    api
      .getProjects(true)
      .then((response) => {
        if (superseded()) return
        if (response && response.data) {
          setProjects(response.data)
        }
        setHasFetched(true)
        setLoading(false)
      })
      .catch((error) => {
        if (superseded()) return
        setLoading(false)
        displayErrorMessage(error)
      })
  }, [user, displayErrorMessage])

  const reload = () => {
    setRevision((current) => current + 1)
    fetchProjects()
  }
  // The socket handlers outlive renders; they reach the current user's reload through this.
  const latestReload = useRef(reload)
  latestReload.current = reload

  // Fetch for any signed-in user, not only on /home. The sidebar renders the
  // hub list on every route, so gating the fetch on the home route meant a
  // direct load of anything else — /settings/security, a document link from an
  // email — drew the HUBS section with no hubs under it. `hasFetched` still
  // keeps this to one request per session; the route is simply not what decides
  // whether the data is needed. Later changes arrive as `hubs_updated` (below).
  useEffect(() => {
    if (!hasFetched && user && user.publicId) {
      fetchProjects()
    }
  }, [hasFetched, user, fetchProjects])

  useEffect(() => {
    if (!user?.publicId) {
      setProjects([])
    }
    setHasFetched(false)
  }, [user?.publicId])

  // A change to hubs or sites made anywhere (another user, another tab) that alters this user's hub
  // list is sent to their own room as `hubs_updated`: re-read the list, once per burst.
  useEffect(() => {
    const userId = user?.publicId
    if (!userId) return
    const burst = createResourcesUpdatedBurst(
      () => latestReload.current(),
      RESOURCES_UPDATED_WINDOW_MS + Math.random() * HUBS_UPDATED_JITTER_MS
    )
    const onHubsUpdated = () => burst.push(null)

    // A reconnect may have missed events: re-read.
    const leaveRoom = subscribeSocketRoom(socket, userId, onHubsUpdated)
    socket.on('hubs_updated', onHubsUpdated)

    return () => {
      leaveRoom()
      burst.cancel()
      socket.off('hubs_updated', onHubsUpdated)
    }
  }, [user?.publicId])

  const value: HubsDataStore = {
    projects,
    loading,
    reload,
    revision
  }

  return <HubsContext.Provider value={value}>{children}</HubsContext.Provider>
}

export const useHubsData = (): HubsDataStore => {
  const ctx = React.useContext(HubsContext)
  if (!ctx) throw new Error('useHubsData can only be used within a HubsDataProvider')
  return ctx
}
